import { Component, signal } from '@angular/core';
import { NotificationService } from '../../notification/notification.service';
import { SessionService } from '@shared/session.service';
import { SocketService } from '@shared/socket.service';

type BanDurationHours = 1 | 8 | 12 | 24 | 48 | 72;

interface BanDurationOption {
  hours: BanDurationHours;
  label: string;
}

@Component({
  selector: 'app-profile-self-exclusion',
  templateUrl: './profile-self-exclusion.component.html',
  styleUrls: ['./profile-self-exclusion.component.scss'],
})
export class ProfileSelfExclusionComponent {
  // TODO(migration): the `time` value sent to `self_ban` is UNVERIFIED against the backend — it
  // may expect minutes rather than hours (a 60x risk on a responsible-gambling control; admin bans
  // in both SkinBet and tf2-hunt are minutes). Do not treat the labels below as accurate until BE
  // confirms the unit. See P4 report / spec AC-22.
  readonly durationOptions: BanDurationOption[] = [
    { hours: 1, label: '1 hour' },
    { hours: 8, label: '8 hours' },
    { hours: 12, label: '12 hours' },
    { hours: 24, label: '24 hours' },
    { hours: 48, label: '2 days' },
    { hours: 72, label: '3 days' },
  ];

  selectedDuration = signal<BanDurationHours | null>(null);
  understandsConsequences = signal(false);
  submitting = signal(false);

  constructor(private socketService: SocketService, private notificationsService: NotificationService, private session: SessionService) {}

  selectDuration(hours: BanDurationHours) {
    this.selectedDuration.set(hours);
  }

  toggleUnderstand() {
    this.understandsConsequences.update((value) => !value);
  }

  canSubmit(): boolean {
    return this.selectedDuration() !== null && this.understandsConsequences() && !this.submitting();
  }

  async submit() {
    const time = this.selectedDuration();
    if (time === null || !this.understandsConsequences() || this.submitting()) {
      return;
    }

    this.submitting.set(true);
    try {
      const message = await this.socketService.emit<string>('self_ban', { time }, { timeout: 5000 });
      this.notificationsService.success(message || 'Self-exclusion enabled.', 'Self-Exclusion');
      this.session.logout();
    } catch {
      // socketService already toasted the error/timeout — stay on the page so the user can retry,
      // deliberately not fire-and-forget like tf2's own call.
    } finally {
      this.submitting.set(false);
    }
  }
}
